import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Challenge from '../../model/Challenge'
import { useChallengeId, useSetActiveChallengeId } from '../../hooks/challenge/useChallengeId'
import { useChallenges } from '../../hooks/challenge/useChallenges'
import { useEntitledChallengeIds } from '../../hooks/membership/useEntitledChallengeIds'
import { useTeamMemberships } from '../../hooks/membership/useTeamMemberships'
import { ScreenPath } from '../../navigation'
import AppIcon from '../icons/AppIcon'
import Logo from '../primitives/Logo'
import Modal from '../primitives/Modal'
import ChallengeMark from './ChallengeMark'
import ChallengeStartsIn from './ChallengeStartsIn'
import styles from './ChallengeSwitcher.module.css'

export interface ChallengeSwitcherProps {
  challengeName?: string
  challengeLogoUrl?: string | null
}

function challengeStatus(challenge: Challenge, isFreeAgent: boolean): string | null {
  if (isFreeAgent) return 'Free agent'
  if (challenge.hasEnded()) return 'Ended'
  if (challenge.hasStarted()) return 'In progress'
  return null
}

export default function ChallengeSwitcher({
  challengeName,
  challengeLogoUrl,
}: ChallengeSwitcherProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const activeChallengeId = useChallengeId()
  const setActiveChallengeId = useSetActiveChallengeId()
  const { data: challenges } = useChallenges()
  const { data: memberships } = useTeamMemberships()
  const { data: entitledIds } = useEntitledChallengeIds()

  const memberChallengeIds = useMemo(
    () => new Set(memberships?.map((m) => m.challengeID) ?? []),
    [memberships],
  )

  const yourChallengeIds = useMemo(() => {
    const ids = new Set(memberChallengeIds)
    for (const id of entitledIds ?? []) ids.add(id)
    return ids
  }, [memberChallengeIds, entitledIds])

  const yourChallenges = useMemo(() => {
    const listed = (challenges ?? []).filter((c) => yourChallengeIds.has(c.id))
    return listed.sort((a, b) => {
      if (a.id === activeChallengeId) return -1
      if (b.id === activeChallengeId) return 1
      return a.name.localeCompare(b.name)
    })
  }, [challenges, yourChallengeIds, activeChallengeId])

  const showJoinAnother = (challenges ?? []).some((c) => !yourChallengeIds.has(c.id))
  const canSwitch = yourChallenges.length > 1 || showJoinAnother

  if (!canSwitch) {
    return <Logo name={challengeName} logoUrl={challengeLogoUrl} />
  }

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        aria-label="Switch challenge"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Logo
          name={challengeName}
          logoUrl={challengeLogoUrl}
          endAdornment={
            <span className={styles.chevron} aria-hidden>
              <AppIcon icon="mdi:chevron-down" size={18} />
            </span>
          }
        />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Switch challenge" titleAlign="left">
        <div className={styles.sheet}>
          <p className={styles.sectionLabel}>Your challenges</p>
          <ul className={styles.list}>
            {yourChallenges.map((challenge) => {
              const active = challenge.id === activeChallengeId
              const isFreeAgent = !memberChallengeIds.has(challenge.id)
              const status = challengeStatus(challenge, isFreeAgent)
              return (
                <li key={challenge.id}>
                  <button
                    type="button"
                    className={[styles.row, active ? styles.rowActive : ''].filter(Boolean).join(' ')}
                    onClick={() => {
                      const switching = challenge.id !== activeChallengeId
                      setActiveChallengeId(challenge.id)
                      setOpen(false)
                      if (switching) navigate(ScreenPath.home)
                    }}
                  >
                    <ChallengeMark challenge={challenge} className={styles.markSlot} />
                    <span className={styles.rowText}>
                      <span className={styles.rowName}>{challenge.name}</span>
                      <span className={styles.rowMeta}>
                        {challenge.isTest && <span className={styles.testLabel}>Test</span>}
                        {status ?? (
                          <ChallengeStartsIn startDate={challenge.startDate} />
                        )}
                      </span>
                    </span>
                    {active && (
                      <AppIcon icon="mdi:check" size={20} className={styles.check} />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>

          {showJoinAnother && (
            <button
              type="button"
              className={styles.join}
              onClick={() => {
                setOpen(false)
                navigate(ScreenPath.challenges)
              }}
            >
              <span className={styles.joinIcon} aria-hidden>
                <AppIcon icon="mdi:plus" size={18} />
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowName}>Join another challenge</span>
                <span className={styles.rowMeta}>Browse open challenges</span>
              </span>
              <AppIcon icon="mdi:chevron-right" size={18} className={styles.joinChevron} />
            </button>
          )}
        </div>
      </Modal>
    </>
  )
}
